import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button, cn } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import {
  istToday,
  monthKey,
  monthTitle,
  parseDay,
  shiftMonth,
  useMyTeamAttendance,
  type AttendanceStatus,
  type RegisterDay,
} from "../../lib/team/api";

/** Phase SC-22d — a team member's own month, one cell per day. */

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const TONE: Record<AttendanceStatus, string> = {
  present: "bg-primary text-primary-foreground",
  half_day: "bg-primary/40 text-foreground",
  absent: "bg-destructive/15 text-destructive",
  leave: "bg-amber-200 text-amber-900 dark:bg-amber-800/50 dark:text-amber-100",
  holiday: "bg-muted text-muted-foreground",
  week_off: "bg-muted/60 text-muted-foreground",
};

const LABEL: Record<AttendanceStatus, string> = {
  present: "Present",
  half_day: "Half day",
  absent: "Absent",
  leave: "Leave",
  holiday: "Holiday",
  week_off: "Week off",
};

function shown(d: RegisterDay): AttendanceStatus | null {
  return d.status ?? (d.holiday ? "holiday" : d.week_off ? "week_off" : null);
}

export function TeamAttendance() {
  const today = istToday();
  const [ty, tm] = parseDay(today);
  const [[year, month], setYm] = React.useState<[number, number]>([ty, tm]);
  const query = useMyTeamAttendance(monthKey(year, month));
  const isCurrent = year === ty && month === tm;
  const days = query.data?.days ?? [];
  const firstDay = days[0]?.day;
  const lead = firstDay
    ? (() => {
        const [y, m, d] = parseDay(firstDay);
        return (new Date(y, m - 1, d).getDay() + 6) % 7;
      })()
    : 0;

  return (
    <div className="space-y-5">
      <Reveal>
        <div className="flex items-center justify-between gap-3">
          <SectionHeader title="Attendance" level={1} />
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-9 rounded-full"
              aria-label="Previous month"
              onClick={() => setYm(shiftMonth(year, month, -1))}
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </Button>
            <span className="min-w-28 text-center text-sm font-medium" aria-live="polite">
              {monthTitle(year, month)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="size-9 rounded-full"
              aria-label="Next month"
              disabled={isCurrent}
              onClick={() => setYm(shiftMonth(year, month, 1))}
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </div>
      </Reveal>

      <Reveal index={1}>
        <SectionCard className="space-y-4">
          {query.isError ? (
            <div role="alert" className="flex items-center justify-between gap-3 text-sm">
              <span className="text-destructive">Couldn&apos;t load this month.</span>
              <Button size="sm" variant="outline" onClick={() => query.refetch()}>
                Retry
              </Button>
            </div>
          ) : null}
          <div className="grid grid-cols-4 gap-2 text-center">
            {(
              [
                ["Present", query.data?.present],
                ["Absent", query.data?.absent],
                ["Leave", query.data?.leave],
                ["Waiting", query.data?.pending],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="bg-muted/50 rounded-xl px-1 py-2">
                <p className="font-display text-lg font-semibold tabular-nums">{value ?? "—"}</p>
                <p className="text-muted-foreground text-[0.7rem]">{label}</p>
              </div>
            ))}
          </div>

          <div>
            <div aria-hidden="true" className="mb-1 grid grid-cols-7 gap-1.5">
              {WEEKDAYS.map((w, i) => (
                <span
                  key={`${w}-${i}`}
                  className="text-muted-foreground text-center text-[0.7rem] font-medium"
                >
                  {w}
                </span>
              ))}
            </div>
            <ol
              aria-label={`Attendance for ${monthTitle(year, month)}`}
              className="grid grid-cols-7 gap-1.5"
            >
              {Array.from({ length: lead }).map((_, i) => (
                <li key={`lead-${i}`} aria-hidden="true" />
              ))}
              {query.isLoading
                ? Array.from({ length: 30 }).map((_, i) => (
                    <li
                      key={`sk-${i}`}
                      aria-hidden="true"
                      className="bg-muted/60 aspect-square rounded-lg motion-safe:animate-pulse"
                    />
                  ))
                : days.map((d) => {
                    const s = shown(d);
                    const pending = d.approval_status === "pending";
                    return (
                      <li
                        key={d.day}
                        aria-label={`${d.day}: ${s ? LABEL[s] : "Not marked"}${pending ? ", waiting for approval" : ""}`}
                        className={cn(
                          "grid aspect-square place-items-center rounded-lg text-xs font-medium tabular-nums",
                          s ? TONE[s] : "border-border/60 text-muted-foreground border",
                          pending && "ring-offset-card ring-2 ring-amber-400 ring-offset-1",
                          d.day === today &&
                            "outline-primary/50 outline outline-2 outline-offset-2",
                        )}
                      >
                        {Number(d.day.slice(8))}
                      </li>
                    );
                  })}
            </ol>
          </div>

          <ul className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-[0.7rem]">
            {(["present", "half_day", "absent", "leave", "week_off"] as AttendanceStatus[]).map(
              (s) => (
                <li key={s} className="flex items-center gap-1.5">
                  <span aria-hidden="true" className={cn("size-2.5 rounded-sm", TONE[s])} />
                  {LABEL[s]}
                </li>
              ),
            )}
            <li className="flex items-center gap-1.5">
              <span aria-hidden="true" className="size-2.5 rounded-sm ring-2 ring-amber-400" />
              Waiting for approval
            </li>
          </ul>
        </SectionCard>
      </Reveal>
    </div>
  );
}
